"use client";

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import type { ExpenseDashboard } from "@/lib/api-client";
import { useAppStore } from "@/lib/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DollarSign,
  Wallet,
  Receipt,
  Users,
  TrendingUp,
  PieChart,
  CalendarDays,
} from "lucide-react";
import { isStaffRole, formatPrice, formatDate  } from "@/components/booking/format";
import { AvailabilityCalendar } from "@/components/booking/availability-calendar";
import { useI18n } from "@/lib/i18n/provider";

export function DashboardView() {
  const user = useAppStore((s) => s.user);
  const { t, locale } = useI18n();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => apiGet<ExpenseDashboard>("/api/admin/dashboard"),
    enabled: !!user && (isStaffRole(user.role)),
    refetchInterval: 20000,
  });

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <Skeleton className="mb-6 h-8 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const { summary, payments, byPayer, byCategory, pendingExpenses } = data;
  const maxPayerTotal = Math.max(1, ...byPayer.map((p) => p.total));
  const maxCategoryTotal = Math.max(1, ...byCategory.map((c) => c.total));

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <PieChart className="h-6 w-6 text-violet-600" /> {t("dash.title")}
        </h1>
        <p className="mt-1 text-muted-foreground">{t("dash.subtitle")}</p>
      </div>

      {/* Summary */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          icon={DollarSign}
          label={t("dash.summaryTotalPaid")}
          value={formatPrice(summary.totalPaid)}
          color="text-emerald-600 bg-emerald-50"
        />
        <SummaryCard
          icon={Wallet}
          label={t("dash.summaryTotalPending")}
          value={formatPrice(summary.totalPending)}
          color="text-amber-600 bg-amber-50"
        />
        <SummaryCard
          icon={Receipt}
          label={t("dash.summaryPayments")}
          value={String(summary.paymentsCount)}
          color="text-sky-600 bg-sky-50"
        />
        <SummaryCard
          icon={Users}
          label={t("dash.summaryPendingUsers")}
          value={String(summary.pendingUsersCount)}
          color="text-violet-600 bg-violet-50"
        />
      </div>

      {/* Availability & booking calendar */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarDays className="h-4 w-4" /> {t("calendar.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AvailabilityCalendar />
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* By payer */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4" /> {t("dash.byPayer")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {byPayer.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dash.noContributions")}</p>
            ) : (
              byPayer.map((p) => (
                <div key={p.name}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-medium">{p.name}</span>
                    <span className="text-muted-foreground">
                      {formatPrice(p.total)} · {p.count} {p.count === 1 ? "payment" : "payments"}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-emerald-500"
                      style={{ width: `${(p.total / maxPayerTotal) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* By category */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <PieChart className="h-4 w-4" /> {t("dash.byCategory")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {byCategory.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dash.noContributions")}</p>
            ) : (
              byCategory.map((c) => (
                <div key={c.category}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-medium">{t(`expenses.cat.${c.category}`)}</span>
                    <span className="text-muted-foreground">
                      {formatPrice(c.total)} · {c.count}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-violet-500"
                      style={{ width: `${(c.total / maxCategoryTotal) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent payments */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">{t("dash.recentPayments")}</CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("dash.noPayments")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4">{t("expenses.paidDate")}</th>
                    <th className="pb-2 pr-4">{t("expenses.paidBy")}</th>
                    <th className="pb-2 pr-4">{t("expenses.category")}</th>
                    <th className="pb-2 pr-4">{t("expenses.description")}</th>
                    <th className="pb-2 pr-4">{t("expenses.paymentMethod")}</th>
                    <th className="pb-2 text-right">{t("expenses.amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 text-muted-foreground">
                        {p.paidDate ? formatDate(p.paidDate, locale) : "—"}
                      </td>
                      <td className="py-2 pr-4 font-medium">{p.paidBy?.name ?? "—"}</td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline">{t(`expenses.cat.${p.category}`)}</Badge>
                      </td>
                      <td className="py-2 pr-4 max-w-[200px] truncate" title={p.description}>
                        {p.description}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {p.paymentMethod ? t(`expenses.method.${p.paymentMethod}`) : "—"}
                      </td>
                      <td className="py-2 text-right font-medium">{formatPrice(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending expenses */}
      {pendingExpenses.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">{t("dash.pendingExpenses")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingExpenses.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 text-sm"
              >
                <div>
                  <Badge variant="outline">{t(`expenses.cat.${e.category}`)}</Badge>
                  <span className="ml-2">{e.description}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{e.productTitle}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {t("expenses.dueDate")} {formatDate(e.dueDate, locale)}
                  </span>
                  <span className="font-medium">{formatPrice(e.amount)}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-semibold leading-tight">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}
